import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Field, FieldArrayRenderProps } from "@progress/kendo-react-form";
import {
  CustomAppDialog,
  CustomDialogCloseButton,
} from "../../components/dialog/Dialog";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { MdDelete } from "react-icons/md";
import IconButton from "../../components/common/IconButton";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import {
  closeBandhaiReceiptChargesDetailDeleteConfirmDialog,
  openBandhaiReceiptChargesDetailDeleteConfirmDialog,
  setBandhaiReceiptChargesDetailIndex,
} from "./bandhaiReceiptSlice";
import { ICharges } from "../charges/chargesModel";

const BandhaiReceiptChargesArray = (
  fieldArrayRenderProps: FieldArrayRenderProps,
) => {
  const dispatch = useAppDispatch();
  const BandhaiReceiptChargesDetailIndex = useAppSelector(
    (state) => state.bandhaireceipt.BandhaiReceiptChargesDetailIndex,
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const isViewDialog = dialogName.startsWith("view-bandhai-charges-");
  const BandhaiReceiptChargesDetailDeleteConfirmDialog = useAppSelector(
    (state) =>
      state.bandhaireceipt.BandhaiReceiptChargesDetailDeleteConfirmDialog,
  );
  const fieldName = fieldArrayRenderProps.name;
  const ChargesList = useAppSelector((state) => state.charges.ChargesList);

  const handleDeleteAction = React.useCallback(
    async (index: any) => {
      if (fieldArrayRenderProps?.value[index]?.id) {
        fieldArrayRenderProps.onRemove({ index: index });
        dispatch(closeBandhaiReceiptChargesDetailDeleteConfirmDialog());
        dispatch(setBandhaiReceiptChargesDetailIndex(-1));
      } else {
        fieldArrayRenderProps.onRemove({ index: index });
        dispatch(closeBandhaiReceiptChargesDetailDeleteConfirmDialog());
        dispatch(setBandhaiReceiptChargesDetailIndex(-1));
      }
    },
    [fieldArrayRenderProps],
  );

  const pushElementInarray = React.useCallback(() => {
    fieldArrayRenderProps?.onPush({
      value: {
        item_id: null,
        qty: 0,
        rate: 0,
        total: 0,
      },
    });
  }, [fieldArrayRenderProps]);

  const handleOpenDeleteDialog = (index: any) => {
    dispatch(openBandhaiReceiptChargesDetailDeleteConfirmDialog());
    dispatch(setBandhaiReceiptChargesDetailIndex(index));
  };

  return (
    <>
      <CustomAppDialog open={BandhaiReceiptChargesDetailDeleteConfirmDialog}>
        <>
          <Typography.h5>{"Delete Bandhai Charges"}</Typography.h5>
          <GridLayout>
            <GridLayoutItem>
              <Typography.p>Are you sure you want to delete this?</Typography.p>
            </GridLayoutItem>
            <GridLayoutItem
              style={{
                display: "flex",
                justifyContent: "end",
              }}
            >
              <CustomDialogCloseButton
                themeColor="error"
                closeDialogFunction={
                  closeBandhaiReceiptChargesDetailDeleteConfirmDialog
                }
              />
              <Button
                fillMode={"solid"}
                themeColor={"error"}
                onClick={() =>
                  handleDeleteAction(BandhaiReceiptChargesDetailIndex)
                }
                type="button"
              >
                Delete
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </>
      </CustomAppDialog>
      <GridLayout cols={[{ width: "50%" }, { width: "50%" }]}>
        <GridLayoutItem>
          <Typography.h5 style={{ marginBottom: 5 }}>
            Bandhai Charges Detail
          </Typography.h5>
        </GridLayoutItem>
        <GridLayoutItem style={{ textAlign: "left", marginLeft: "auto" }}>
          {!isViewDialog && (
            <Button
              fillMode={"solid"}
              themeColor={"primary"}
              onClick={pushElementInarray}
              type="button"
            >
              Add Item
            </Button>
          )}
        </GridLayoutItem>
      </GridLayout>
      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps.value.length > 0 && (
          <div
            className="table-responsive"
            style={{
              maxHeight: isViewDialog ? "50vh" : "20vh",
              scrollbarWidth: "thin",
              marginTop: 10,
            }}
          >
            <table
              className="table table-striped table-bordered table-hover field-array-table"
              style={{ minWidth: "100%" }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      width: "13%",
                    }}
                  >
                    Charges Name
                  </th>
                  <th style={{ width: "7%" }}>Pcs</th>
                  <th style={{ width: "10%" }}>Rate</th>
                  <th style={{ width: "10%" }}>Total</th>
                  <th style={{ width: "3%", position: "sticky" }}></th>
                </tr>
              </thead>
              <tbody>
                {fieldArrayRenderProps.value &&
                  fieldArrayRenderProps.value.length > 0 &&
                  fieldArrayRenderProps.value.map(
                    (item_used_by_kariagr: any, index: number) => (
                      <tr>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.item_id`}
                            placeholder="Select Charges"
                            component={FormSelectionField}
                            validator={requiredValidator}
                            options={ChargesList?.map((item: ICharges) => {
                              return {
                                value: item.id,
                                label: item.charges_name,
                              };
                            })}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.qty`}
                            placeholder="0.00"
                            type="number"
                            format="n2"
                            component={FormNumericTextField}
                            validator={requiredValidator}
                            disabled={isViewDialog}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.rate`}
                            placeholder="0.00"
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                            // validator={requiredValidator}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.total`}
                            placeholder="0.00"
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                            disabled={true}
                            // validator={requiredValidator}
                          />
                        </td>
                        <td
                          style={{
                            position: "sticky",
                            right: 0,
                            zIndex: 2,
                          }}
                        >
                          <IconButton
                            type="button"
                            fillMode="solid"
                            size="small"
                            title="Delete"
                            themeColor="error"
                            style={{
                              position: "absolute",
                              height: 32,
                              width: 32,
                              right: 15,
                              pointerEvents: "all",
                            }}
                            onClick={() => handleOpenDeleteDialog(index)}
                          >
                            <MdDelete
                              className="absolute-position"
                              style={{ fontSize: "22px" }}
                            />
                          </IconButton>
                        </td>
                      </tr>
                    ),
                  )}
              </tbody>
            </table>
          </div>
        )}
    </>
  );
};

export default BandhaiReceiptChargesArray;
